import store from '@/store';
import { isSsr } from '@/utilities/Utilities';
import { ConfigData } from '@/Config';
import useAxios from '@/hooks/useAxios';

interface TrackedObject {
  object?: string;
  object_value?: number | string;
  created_on?: number;
  inSession?: boolean;
  distinct?: boolean;
  entries?: TrackedObject[];
  expire?: number;
}

declare global {
  interface Window {
    api_prefix: string;
  }
}

let firstInit = true;

// collection of entries tracked outside the session
const trackedOutsideSession: TrackedObject[] = [];

// lead score tracking session duration in minutes
const sessionDuration = 30;

// Fetch current leadscore session information
let leadscoreSession: TrackedObject | null = null;

export default function useLeadScore() {
  const campaignState = store.getters['campaign/state'];
  const globalState = store.getters['global/state'];

  const initLeadScore = async () => {
    try {
      let sessionFromStorage: string | null = null;
      if (campaignState.config && !firstInit) {
        sessionFromStorage = window.localStorage.getItem('leadscore-' + campaignState.config.campaignId);
      }

      // If leadscore session information was found, parse it
      if (sessionFromStorage !== null && sessionFromStorage) {
        leadscoreSession = JSON.parse(sessionFromStorage);
      } else {
        // If no session was found, create one with an expiration
        leadscoreSession = {
          expire: new Date().getTime() + 1000 * 60 * sessionDuration,
          entries: []
        };
        firstInit = false;
        writeSessionStorage();
      }
    } catch (e) {
      // If exception occur
      leadscoreSession = {
        expire: new Date().getTime() + 1000 * 60 * sessionDuration,
        entries: []
      };
    }
  };

  const checkSessionExpiration = () => {
    // If leadscore session expires.. create a new one
    if (leadscoreSession && leadscoreSession.expire) {
      if (new Date().getTime() >= leadscoreSession.expire) {
        leadscoreSession = {
          expire: new Date().getTime() + 1000 * 60 * sessionDuration,
          entries: []
        };

        writeSessionStorage();
      }
    }
  };

  // Helper method for checking if a object & object value is distinct
  const isDistinct = (object: string, object_value: number | string, collection: TrackedObject[]) => {
    return !collection.some((entry) => entry.object === object && entry.object_value === object_value);
  };

  const writeSessionStorage = () => {
    try {
      if (
        !isSsr() &&
        campaignState.config &&
        campaignState.config.campaignId &&
        campaignState.config.leadScoreEnabled
      ) {
        window.localStorage.setItem('leadscore-' + campaignState.config.campaignId, JSON.stringify(leadscoreSession));
      }
    } catch (e) {
      throw new Error('cant set item in storage: ' + e);
    }
  };

  const optionsIsDistinct = (options: TrackedObject): boolean => {
    if (options.distinct && options.object_value && options.object) {
      if (!options.inSession && !isDistinct(options.object, options.object_value, trackedOutsideSession)) {
        return false;
      } else if (
        leadscoreSession &&
        leadscoreSession.entries &&
        options.inSession &&
        !isDistinct(options.object, options.object_value, leadscoreSession.entries)
      ) {
        return false;
      }
    }
    return true;
  };

  const track = async (options: TrackedObject, disableSendTracked = false, failureCallback?: () => void) => {
    // Early return if leadscore isn't enabled
    if (campaignState.config && !campaignState.config.leadScoreEnabled && isSsr()) {
      return Promise.reject('leadscore not enabled or isSsr');
    }

    await initLeadScore();

    let trackedObjectSent = false;
    let trackedObject: TrackedObject = {};

    options = options || {};

    //Set default values in options
    options.inSession = options.inSession || false;
    options.object = options.object || '';
    options.object_value = options.object_value || undefined;
    options.distinct = options.distinct || false;

    const optionsDistinct = optionsIsDistinct(options);

    if (optionsDistinct) {
      trackedObject = {
        object: options.object,
        object_value: options.object_value,
        created_on: Math.floor(new Date().getTime() / 1000)
      };

      // If it's not to be saved in session.. save it in memory
      if (!options.inSession) {
        trackedOutsideSession.push(trackedObject);
      } else {
        // If we're to save it in session, first check if session expired
        // & if not write to session storage.
        checkSessionExpiration();

        leadscoreSession?.entries?.push(trackedObject);

        writeSessionStorage();
      }

      if (trackedObject.object && ['FLOW_PAGE', 'ADDON'].indexOf(trackedObject.object) !== -1 && !disableSendTracked) {
        trackedObjectSent = await sendTracked();
      }
    }

    return new Promise((resolve, reject) => {
      // if tracked object didn't get sent when it should
      // then we reject it with an message
      // Note: it will only be sent on registration flow
      if (
        !trackedObjectSent &&
        globalState.flowRegistrationInfo &&
        globalState.flowRegistrationInfo?.id &&
        campaignState.config?.leadScoreEnabled
      ) {
        if (failureCallback) {
          failureCallback();
        }
        reject('something went wrong with sending the tracked object');
      }

      resolve(trackedObject);
    });
  };

  const sendTracked = async () => {
    if (
      !globalState.flowRegistrationInfo ||
      !globalState.flowRegistrationInfo?.id ||
      !campaignState.config?.leadScoreEnabled
    ) {
      return false;
    }

    const postUrl = `${ConfigData.apiEndpoint}/api/v1/campaign/leadscore?r=${new Date().getTime()}`;

    const postData = {
      campaign_id: campaignState.config?.campaignId,
      customer_id: campaignState.config?.customerId,
      registration_id: globalState.flowRegistrationInfo?.id,
      tracking: getTracked()
    };
    const { postDataFormData } = useAxios(postUrl, postData);
    let response;
    try {
      response = await postDataFormData();
    } catch (e) {
      return false;
    }

    return !!response;
  };

  const getTracked = () => {
    const collective: TrackedObject[] = [];

    trackedOutsideSession.forEach((localTracked) => {
      collective.push(localTracked);
    });

    if (leadscoreSession?.entries) {
      leadscoreSession?.entries.forEach((sessionTracked) => {
        collective.push(sessionTracked);
      });
    }

    collective.sort((a, b) => {
      if (a && b && a.created_on && b.created_on && a.created_on < b.created_on) {
        return -1;
      } else if (a && b && a.created_on && b.created_on && a.created_on > b.created_on) {
        return 1;
      }
      return 0;
    });

    return collective;
  };

  return {
    track,
    getTracked,
    sendTracked
  };
}
